import { Dispatch, SetStateAction, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonSearchbar,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RequestModel, SubModel } from "model";
import { useSubProfile } from "pages/SubProfile/hooks";
// FIXME: Change this to use `Icon` from `swing-components`
import { MdArrowBack, MdChevronRight } from "react-icons/md";
import { Alert, Badge, Icon, useModal } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { SubProfile } from "~components/swing-react-components";
import {
  COMBINED_RESTRICTIONS,
  confirmWaitlistedSub,
  translate,
  useShouldRestrictAdmin,
} from "~utils";

import { SubInfoCard } from "../SubInfoCard/SubInfoCard";
import styles from "./SubWaitlistSelectionView.module.css";

type SubWaitlistSelectionViewProps = {
  request: RequestModel;
};

export function SubWaitlistSelectionView({ request }: SubWaitlistSelectionViewProps) {
  const hasChooseSubRestriction = useShouldRestrictAdmin([COMBINED_RESTRICTIONS.CHOOSE_SUB]);

  const [selectedSubId, setSelectedSubId] = useState("");
  const [formState, setFormState] = useState<SubSelectionModalStates>("subSelection");

  const refreshForm = () => {
    setSelectedSubId("");
    setFormState("subSelection");
  };

  const { openModal, closeModal } = useModal({
    component: (
      <SubSelectionModal
        dismissModal={() => closeModal()}
        formState={formState}
        setFormState={setFormState}
        requestId={request.id}
        pool={request.pool}
        selectedSubId={selectedSubId}
        setSelectedSubId={setSelectedSubId}
        refreshForm={refreshForm}
      />
    ),
    modalOptions: {
      onDidDismiss: refreshForm,
    },
  });

  if (hasChooseSubRestriction) {
    return <></>;
  }

  const onNameClick = (subId: string) => {
    setFormState("subDetails");
    setSelectedSubId(subId);
    openModal();
  };

  return (
    <SubInfoCard
      status="WAITLIST"
      waitlistSubs={request.pool.map((sub) => ({ profileImageUrl: sub.photo }))}
      isChooseSub
    >
      <h3 css={{ margin: 0 }}>
        <span>{request.pool.length}</span>{" "}
        {request.pool.length === 1
          ? translate("WAITLIST_SUB_COUNT_LABEL_SINGLE")
          : translate("WAITLIST_SUB_COUNT_LABEL_MULTIPLE")}
      </h3>
      <hr css={{ border: "none", height: 2, background: "var(--swing-slate200)" }} />
      {request.pool.length === 0 && (
        <>
          <div css={{ fontSize: 14, padding: "16px 0" }}>
            <p css={{ fontWeight: "bold", margin: 0 }}>
              <span>{translate("WAITLIST_AUTO_FILL_HEADER")}</span> :{" "}
              {request?.waitlistAutoFill ? translate("LABEL_YES") : translate("LABEL_NO")}
            </p>
            {request?.waitlistAutoFill && (
              <p css={{ margin: "8px 0 0 0" }}>{translate("WAITLIST_AUTO_FILL_LABEL")}</p>
            )}
          </div>
          <hr css={{ border: "none", height: 2, background: "var(--swing-slate200)" }} />
          <div>
            <p css={{ fontWeight: "bold" }}>{translate("SUB_INFO_CARD_SEARCHING_MESSAGE")}</p>
            <img
              css={{ width: 128, height: 128 }}
              src="assets/human-binocular.png"
              alt="human with binoculars"
            />
          </div>
        </>
      )}
      {request.pool.slice(0, 5).map((sub) => (
        <div key={sub.id}>
          <div className="flex gap-2">
            <div
              css={{
                width: "fit-content",
                borderRadius: 9999,
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              <img
                src={sub?.photo}
                css={{
                  width: 50,
                  height: 50,
                  borderRadius: "100%",
                  marginTop: "auto",
                  marginBottom: "auto",
                  objectFit: "cover",
                }}
              />
            </div>
            <div>
              <p
                css={{
                  fontSize: 14,
                  margin: "0 0 4px 0",
                  fontWeight: "bold",
                  color: "var(--swing-blue500)",
                  cursor: "pointer",
                }}
                onClick={() => onNameClick(sub.id)}
              >
                {sub.name}
              </p>
              <p css={{ fontSize: 12, margin: 2 }}>
                {sub.districtDaysWorked > 0 ? (
                  <>
                    <span css={{ fontWeight: "bold" }}>{sub.districtDaysWorked} </span>
                    {translate("WAITLIST_N_DAYS_WORKED")}
                  </>
                ) : (
                  translate("WAITLIST_NO_DAYS_WORKED")
                )}
              </p>
              {sub?.hasConflict === false && (
                <Badge status="success" text={translate("WAITLIST_INSTANT_FILL_LABEL")} />
              )}
            </div>
            <div className="flex gap-2" css={{ marginRight: 0, marginLeft: "auto" }}>
              {sub.isSchoolFavorited && <Icon name="Favorite Filled" color="var(--swing-pink)" />}
              {sub.isSwingHero && <Icon name="Swing Hero" color="var(--swing-pink)" />}
            </div>
          </div>
          <hr
            css={{
              border: "none",
              height: 2,
              background: "var(--swing-slate200)",
              justifySelf: "end",
            }}
          />
        </div>
      ))}
      {request.pool.length > 5 && (
        <div css={{ textAlign: "center", padding: "16px 16px 0px 16px", fontWeight: "bold" }}>
          + {request.pool.length - 5}{" "}
          {request.pool.length === 6
            ? translate("WAITLIST_MORE_SUB")
            : translate("WAITLIST_MORE_SUBS")}
        </div>
      )}
      {request.pool.length > 0 && (
        <div css={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
          <IonButton onClick={() => openModal()}>{translate("CHOOSE_SUB_LABEL")}</IonButton>
        </div>
      )}
    </SubInfoCard>
  );
}

type SubSelectionModalStates = "subSelection" | "confirm" | "subDetails";

type SubSelectionModalProps = {
  selectedSubId: string;
  setSelectedSubId: Dispatch<SetStateAction<string>>;
  formState: SubSelectionModalStates;
  setFormState: Dispatch<SetStateAction<SubSelectionModalStates>>;
  requestId: string;
  pool: SubModel[];
  refreshForm: () => void;
  dismissModal: () => void;
};

function SubSelectionModal(props: SubSelectionModalProps) {
  const {
    refreshForm,
    requestId,
    pool,
    formState,
    setFormState,
    selectedSubId,
    setSelectedSubId,
    dismissModal,
  } = props;

  const [hasApiError, setHasApiError] = useState(false);

  const queryClient = useQueryClient();

  const selectedSub = pool.find((item) => item.id === selectedSubId);

  const subName = selectedSub?.name || "";
  const hasConflict = selectedSub?.hasConflict || false;

  const confirmModal = () => {
    setFormState("confirm");
  };

  const { mutate: postConfirmation } = useMutation({
    mutationFn: () => {
      return confirmWaitlistedSub(requestId, selectedSubId);
    },
    onSuccess: () => {
      dismissModal();
      queryClient.invalidateQueries();
    },
    onError: (err) => {
      setHasApiError(true);
      // eslint-disable-next-line no-console
      console.error(err);
    },
  });

  const confirmSub = () => {
    postConfirmation();
  };

  return (
    <div className="modal-wrapper">
      {formState === "subSelection" && (
        <SubSelectionFormStage
          selectedSubId={selectedSubId}
          onSubChange={setSelectedSubId}
          model={pool}
          dismissModal={dismissModal}
          confirmModal={confirmModal}
          goToSubDetailsStep={() => setFormState("subDetails")}
        />
      )}
      {formState === "confirm" && (
        <SubConfirmFormStage
          hasApiError={hasApiError}
          onSetHasApiError={(value) => setHasApiError(value)}
          subName={subName}
          subHasConflict={hasConflict}
          dismissModal={dismissModal}
          onConfirm={confirmSub}
        />
      )}
      {formState === "subDetails" && (
        <SubInfoModal dismiss={refreshForm} subId={selectedSubId} confirmModal={confirmModal} />
      )}
    </div>
  );
}

type SubInfoModalProps = {
  dismiss: () => void;
  confirmModal: () => void;
  subId?: string;
};

function SubInfoModal(props: SubInfoModalProps) {
  const { dismiss, subId, confirmModal } = props;

  /*** Query ***/
  const subProfile = useSubProfile(subId || "");

  /*** Render ***/
  // TODO: Should we add a loader?
  if (!subId) return <></>;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={dismiss}>
              <MdArrowBack className="swing-icon-tooltip icon-2xl" />
            </IonButton>
          </IonButtons>
          <IonTitle>{translate("SUB_PROFILE_LABEL")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <SubProfile {...subProfile} isSwingHero={subProfile?.stats?.isSwingHero} />
        <div className={styles.bottomHoverSection}>
          <IonButton
            expand="block"
            onClick={() => {
              confirmModal();
            }}
          >
            {translate("SELECT_THIS_SUB_LABEL")}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
}

type SubSelectionFormStageProps = {
  selectedSubId: string;
  onSubChange: Dispatch<SetStateAction<string>>;
  model: SubModel[];
  dismissModal: () => void;
  confirmModal: () => void;
  goToSubDetailsStep: () => void;
};

const SubSelectionFormStage: React.FC<SubSelectionFormStageProps> = ({
  selectedSubId,
  onSubChange,
  model,
  dismissModal,
  confirmModal,
  goToSubDetailsStep,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translate("CHOOSE_SUB_LABEL")}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => dismissModal()}>
              <Icon name="Close"></Icon>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonList className={`${styles.listFill}`}>
          <IonSearchbar
            className={styles.searchbar}
            debounce={250}
            value={searchTerm}
            onIonInput={(e) => {
              setSearchTerm(e?.target?.value || "");
            }}
          />
          <IonRadioGroup
            value={selectedSubId}
            onIonChange={(e) => {
              onSubChange(e.target.value);
            }}
          >
            <div>
              {/* TODO: Remove wrapper div when Ionic comes out with a fix for 
                  https://github.com/ionic-team/ionic-framework/issues/30359 */}
              {model
                .filter((subName) => subName.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((sub) => (
                  <IonItem lines="inset" key={sub.id}>
                    <IonRadio slot="start" value={sub.id} aria-label={sub.id} />
                    <IonLabel
                      onClick={() => {
                        onSubChange(sub.id);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 overflow-ellipsis">
                          <span css={{ maxWidth: 200 }}>{sub.name}</span>
                          {sub.isSchoolFavorited && (
                            <Icon name="Favorite Filled" color="var(--swing-pink)" />
                          )}
                          {sub.isSwingHero && <Icon name="Swing Hero" color="var(--swing-pink)" />}
                          {sub?.languages?.length > 0 && (
                            <LanguagesLabel languages={sub.languages} />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {sub?.hasConflict === false && (
                            <Badge
                              status="success"
                              text={translate("WAITLIST_INSTANT_FILL_LABEL")}
                            />
                          )}
                          <IonButton
                            className={styles.visitProfileButton}
                            fill="clear"
                            onClick={() => {
                              onSubChange(sub.id);
                              goToSubDetailsStep();
                            }}
                          >
                            <MdChevronRight className="icon-2xl" />
                          </IonButton>
                        </div>
                      </div>
                    </IonLabel>
                  </IonItem>
                ))}
            </div>
          </IonRadioGroup>
          <div className={styles.submitButtonHolder}>
            <IonButton
              id="subSelectionState"
              disabled={selectedSubId.length < 1}
              className="w-full ion-no-margin"
              onClick={confirmModal}
            >
              {translate("CHOOSE_SUB_LABEL")}
            </IonButton>
          </div>
        </IonList>
      </IonContent>
    </IonPage>
  );
};

type SubConfirmFormStageProps = {
  subName: string;
  subHasConflict: boolean;
  hasApiError: boolean;
  onSetHasApiError: (hasError: boolean) => void;
  dismissModal: () => void;
  onConfirm: () => void;
};

const SubConfirmFormStage: React.FC<SubConfirmFormStageProps> = ({
  subName,
  subHasConflict,
  hasApiError,
  onSetHasApiError,
  dismissModal,
  onConfirm,
}) => {
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translate("CONFIRM_SUB_HEADER")}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => dismissModal()}>
              <IonText color="primary">{translate("LABEL_CLOSE")}</IonText>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      {hasApiError && (
        <div className="ion-margin-top">
          <Alert
            color="danger"
            onDismiss={() => onSetHasApiError(false)}
            showIcon
            title={translate("LABEL_SOMETHING_WENT_WRONG")}
            className="mx-4"
          >
            <span>
              {translate("LABEL_CONTACT_SUPPORT_1")}{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href={generateSwingSupportURL("/hc/en-us/requests/new")}
              >
                {translate("LABEL_CONTACT_SUPPORT")}
              </a>{" "}
              {translate("LABEL_CONTACT_SUPPORT_2")}
            </span>
          </Alert>
        </div>
      )}
      <IonContent>
        <IonList className={`${styles.listFill}`} lines="none">
          <IonItem>
            <div>
              <p>{translate("PLEASE_CONFIRM_LABEL")}:</p>
              <p>
                {translate("SELECTING_SUB_LABEL_1")} <span className="font-medium">{subName}</span>{" "}
                {translate("SELECTING_SUB_LABEL_2")}.
              </p>
              {subHasConflict && (
                <p>
                  {translate("COMMITMENTS_LABEL", subName)}
                  <br />
                  <br />
                  {`
                  ${translate("COMMITMENTS_EMAIL_LABEL")}
                  `}
                </p>
              )}
            </div>
          </IonItem>
          <div className={styles.submitButtonHolder}>
            <IonButton className="ion-no-margin w-full" onClick={() => onConfirm()}>
              {translate("LABEL_CONFIRM")}
            </IonButton>
            <IonButton className="w-full ion-no-margin" fill="outline" onClick={dismissModal}>
              {translate("LABEL_NEVERMIND")}
            </IonButton>
          </div>
        </IonList>
      </IonContent>
    </IonPage>
  );
};

function LanguagesLabel({ languages }: { languages: Array<string> }) {
  const label = languages.join(", ");
  return (
    <div className="flex gap-1">
      <Icon name="Website" color="var(--swing-slate600)" />
      {label}
    </div>
  );
}
